const parse = require("cssfontparser");
const _ = require("lodash");
const CanvasUtil = require("./CanvasUtil");

const defaultFontInfo = {
	size: 10,
	family: ["sans-serif"],
};

// T14549: Some browsers round font sizes when you set them to a Canvas
// context, and some don't. This function takes a context.font value and
// returns what the browser would use if it were assigned to a context, which
// we sometimes need for sensitive computations involving font size.
const contextizeFont = function(contextFont) {
	const transientContext = CanvasUtil.getTransientContext();
	if (!transientContext) return contextFont;
	transientContext.font = contextFont;
	return transientContext.font;
};

// contextizeFont except it takes and returns an info object.
const contextizeInfo = function(info) {
	return toInfo(contextizeFont(fromInfo(info)));
};

const toFontInfoCache = {};
// Convert a font string to an object with size, family, bold, italic keys.
// family is an array of strings giving all the possible font families.
// Can also pass a font info object; will get the same object back.
const toInfo = function(contextFont) {
	if (contextFont && contextFont.family && contextFont.size) {
		return _.clone(contextFont);
	}

	contextFont = toContextFont(contextFont);
	if (toFontInfoCache[contextFont]) {
		return _.clone(toFontInfoCache[contextFont]);
	}

	// Firefox does this unexplainable thing where it adds \\ between "roboto
	// slab" so it becomes "roboto\\ slab". This makes the font become the default
	// sans-serif font, which breaks text positioning for roboto slab.
	// For PM: flowery comment of frustration.
	contextFont = contextFont && contextFont.replace(/\\/g, "");
	const parsed = parse(contextFont);
	let fontInfo;
	if (!parsed) {
		fontInfo = defaultFontInfo;
	} else {
		fontInfo = {
			italic: parsed.style === "italic",
			bold: parsed.weight === "bold" || parsed.weight === "bolder",
			size: parsed.size || 10,
			family: parsed.family,
		};
	}

	toFontInfoCache[contextFont] = fontInfo;
	return _.clone(fontInfo);
};

// Convenience wrapper for toInfo(x).size.
const getSize = function(contextFont) {
	return toInfo(contextFont).size;
};

// Convert an object in the form of a toInfo return into a string suitable
// to set as context.font.
const fromInfo = function(fontInfo) {
	if (!fontInfo) {
		return false;
	} else if (typeof fontInfo === "string") {
		return fontInfo;
	}

	let result = fontInfo.size + "px " + fontInfo.family.join(", ");
	if (fontInfo.bold) {
		result = "bold " + result;
	}
	if (fontInfo.italic) {
		result = "italic " + result;
	}
	return result;
};

// Given a context or a context.font, set its size, then return the new font
// string.
const setSize = function(contextFont, newValue) {
	const font = toInfo(contextFont);
	font.size = newValue;
	return fromInfo(font);
};

// Given a context or a context.font, increase its size by the given amount of
// pixels, then return the new font string.
const incSize = function(contextFont, incAmount) {
	const font = toInfo(contextFont);
	font.size += incAmount;
	return fromInfo(font);
};

// Given a context or a context.font, multiply its size by the given value, then
// return the new font string.
const scaleSize = function(contextFont, scaleAmount) {
	const font = toInfo(contextFont);
	font.size *= scaleAmount;
	return fromInfo(font);
};

// Given a context or a context.font, make it bold (or not bold if second
// argument is false), then return the new font string.
const makeBold = function(contextFont, newValue) {
	const font = toInfo(contextFont);
	font.bold = newValue !== false;
	return fromInfo(font);
};

// Given a context or a context.font, make it bold (or not bold if second
// argument is false), then return the new font string.
const makeItalic = function(contextFont, newValue) {
	const font = toInfo(contextFont);
	font.italic = newValue !== false;
	return fromInfo(font);
};

// Helpers

// Given either a string or a canvas context, convert to a context.font value.
const toContextFont = function(contextFont) {
	if (_.isObject(contextFont) && contextFont.fillText && contextFont.font) {
		return contextFont.font;
	}
	return contextFont;
};

module.exports = {
	contextizeFont,
	contextizeInfo,
	toInfo,
	getSize,
	fromInfo,
	setSize,
	incSize,
	scaleSize,
	makeBold,
	makeItalic,
};
